import { useSelector } from '@state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { getWorkbenches, setWorkbenchFilters } from '@state/reducers/workbenchReducer'
import { useEffect, useState } from 'react'
import { WorkbenchCard } from './WorkbenchCard'
import { FormHeader } from '../forms/FormHeader'
import { Dialog, DialogContent, IconButton, Pagination, Tooltip } from '@mui/material'
import { WorkbenchDto } from '@services/dtos'
import { DEFAULT_FILTERS } from '@state/reducers/baseCrudReducer'
import WorkbenchForm from '../forms/WorkbenchForm'
import { UpTransition } from '../animations'
import AddIcon from '@mui/icons-material/Add' 

const PAGE_SIZE = 8

interface Props {
  onClose?: () => void
  onSubmit: (workbench: WorkbenchDto | null) => void
}

export function WorkbenchSelector({ onClose, onSubmit }: Props) {
  const dispatch = useAppDispatch()
  const {
    filters,
    workbenches
  } = useSelector(({ workbench }) => ({
    filters: workbench.crud.filters,
    workbenches: workbench.crud.items,
  }))
  const [selectedWorkbench, setSelectedWorkbench] = useState<WorkbenchDto | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formOpen, setFormOpen] = useState(false)

  useEffect(() => {
    dispatch(setWorkbenchFilters({ limit: PAGE_SIZE }))
    return () => {
      dispatch(setWorkbenchFilters(DEFAULT_FILTERS))
    }
  }, [])
  useEffect(() => {
    dispatch(getWorkbenches())
  }, [filters.page])

  const pageCount = Math.ceil(workbenches.totalCount / PAGE_SIZE)

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (selectedWorkbench) {
            setIsSubmitting(true)
            onSubmit(selectedWorkbench)
          }
        }}
        className="flex flex-col w-full gap-4"
      >
        <FormHeader
          canSubmit={!!selectedWorkbench}
          isSubmitting={isSubmitting}
          onClose={() => onClose?.()}
          title="Sélection poste de travail"
        />

        {workbenches.datas.length === 0 && (
          <p className="text-center text-sm text-gray-400">Aucun poste de travail disponible</p>
        )}
        <div className="flex flex-wrap justify-center gap-4">
          {workbenches.datas.map((workbench) => (
            <WorkbenchCard
              key={workbench.id}
              workbench={workbench}
              selected={selectedWorkbench?.id === workbench.id}
              onSelect={() => setSelectedWorkbench(workbench)}
            />
          ))}
          <Tooltip title="Ajouter une enceinte blindée" placement="top" arrow>
            <IconButton
              onClick={() => setFormOpen(true)}
              sx={{ height: '60px', width: '45px', bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
              className='!rounded-lg'
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </div>

        {pageCount > 1 && (
          <div className="flex justify-center mt-auto pt-2">
            <Pagination
              count={pageCount}
              page={filters.page}
              onChange={(_, value) => dispatch(setWorkbenchFilters({ page: value }))}
              color="primary"
              shape="rounded"
            />
          </div>
        )}
      </form>

      <Dialog 
        fullWidth
        maxWidth="sm"
        open={formOpen}
        TransitionComponent={UpTransition}
        onClose={() => setFormOpen(false)}>
        <DialogContent>
          <WorkbenchForm onClose={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  )
}
